import { LOGOUT } from '../../app/authState'
import {
  ADDRESS_TEXT,
  apiErrorCodes,
  CREDIT_CARD,
  METHOD,
  PATIENT_TEXT,
  PRICE,
  PROVIDER_TEXT,
  SERVICE,
  STATE,
  TIMEZONE,
} from '../../utils/constants'
import { errorCodes, getUserFriendlyErrorMessage } from '../../utils/errormessages'
import {
  ADD_CREDIT_CARD,
  ADD_CREDIT_CARD_ERROR,
  ADD_CREDIT_CARD_SUCCESS,
  BOOK_VISIT,
  BOOK_VISIT_ERROR,
  BOOK_VISIT_SUCCESS,
  DELETE_BOOKING,
  GET_CREDIT_CARDS,
  GET_CREDIT_CARDS_ERROR,
  GET_CREDIT_CARDS_SUCCESS,
  GET_PAYMENT_KEYS,
  GET_PAYMENT_KEYS_ERROR,
  GET_PAYMENT_KEYS_SUCCESS,
  GET_PROVIDER_INFO,
  GET_PROVIDER_INFO_ERROR,
  GET_PROVIDER_INFO_SUCCESS,
  RESCHEDULE_VISIT,
  RESCHEDULE_VISIT_ERROR,
  RESCHEDULE_VISIT_SUCCESS,
  SEARCH_FOR_PATIENT,
  SEARCH_FOR_PATIENT_ERROR,
  SEARCH_FOR_PATIENT_SUCCESS,
  SET_ERROR,
  SET_IS_VISIT_RESCHEDULED_SUCCESS,
  SEARCH_FOR_PROVIDER_AVAILABILITY_ERROR,
  SEARCH_FOR_PROVIDER_AVAILABILITY,
  SEARCH_FOR_PROVIDER_AVAILABILITY_SUCCESS,
  SET_NEW_VISIT_SELECTED_PATIENT,
  CLEAR_NEW_VISIT_SELECTED_PATIENT,
} from './NewVisitConstants'
import { apiErrorCodes as apiErrors } from '../../utils/apiHelper'

/**
 * @typedef {Object} SelectedPatient
 * @property {string} [PatientId] - PatientId
 * @property {string} [FirstName] - Patient's first name
 * @property {string} [LastName] - Patient's last name
 * @property {string} [CreateDateTimeUtc] - Patient's creation date and time
 */
/**
 * @typedef {Object} VisitData
 * @property {boolean} [loading] - Whether the visit operation is currently loading
 * @property {string} [error] - Error message for the visit operation
 * @property {Array} [cards] - Array of credit cards associated with the visit
 * @property {string} [cardErrorMessage] - Error message specific to credit card operations
 * @property {Object} [searchResults] - Patient search results with searchId and results
 * @property {Object} [provider] - Provider information for the visit
 * @property {Object} [searchResultsProviders] - Provider availability search results
 * @property {SelectedPatient|null} [selectedPatient] - Currently selected patient
 */

/**
 * @typedef {Object} NewVisitState
 * @property {Object.<string, VisitData>} visits - Map of booking IDs to visit data
 * @property {Object} paymentKeys - Payment configuration keys (API keys, etc.)
 * @property {boolean} isVisitRescheduledSuccess - Whether a visit was successfully rescheduled
 * @property {Object} [defaultServerErrorMessage] - Default server error message for failed operations
 */

/**
 * Initial state for the new visit reducer
 * @type {NewVisitState}
 */
const initialState = {
  visits: {},
  paymentKeys: {},
  isVisitRescheduledSuccess: false,
}
// @ts-expect-error TS7006 -- MIGRATION: not approved, fix when encountered
const getNotFoundError = (errorCode) => {
  switch (errorCode) {
    case apiErrorCodes.VISIT_TYPE_NOT_FOUND:
      return { message: getUserFriendlyErrorMessage(errorCodes.CANNOT_BOOK_VISIT) }
    case apiErrorCodes.CREDIT_CARD_NOT_FOUND:
      return {
        message: getUserFriendlyErrorMessage(errorCodes.DOES_NOT_EXIST_NO_REFRESH, null, CREDIT_CARD.toLowerCase()),
      }
    case apiErrorCodes.PATIENT_NOT_FOUND:
      return {
        message: getUserFriendlyErrorMessage(errorCodes.DOES_NOT_EXIST_NO_REFRESH, null, PATIENT_TEXT.toLowerCase()),
      }

    case apiErrorCodes.PROVIDER_NOT_FOUND:
      return {
        message: getUserFriendlyErrorMessage(errorCodes.DOES_NOT_EXIST_NO_REFRESH, null, PROVIDER_TEXT.toLowerCase()),
      }
    case apiErrorCodes.SERVICE_TYPE_NOT_FOUND:
      return {
        message: getUserFriendlyErrorMessage(errorCodes.DOES_NOT_EXIST_NO_REFRESH, null, SERVICE.toLowerCase()),
      }
    default:
      return {}
  }
}
// @ts-expect-error TS7006 -- MIGRATION: not approved, fix when encountered
const getValidationError = (errorCode, _state) => {
  switch (errorCode) {
    case apiErrorCodes.DATE_TIME_NOT_VALID:
      return { message: getUserFriendlyErrorMessage(errorCodes.CAN_NOT_SELECT_VISIT_TIME) }
    case apiErrorCodes.METHOD_NOT_VALID:
      return { message: getUserFriendlyErrorMessage(errorCodes.ENTER_VALID, null, METHOD.toLowerCase()) }
    case apiErrorCodes.PRICE_NOT_VALID:
      return { message: getUserFriendlyErrorMessage(errorCodes.ENTER_VALID, null, PRICE.toLowerCase()) }
    case apiErrorCodes.STATE_CODE_NOT_VALID:
      return { message: getUserFriendlyErrorMessage(errorCodes.ENTER_VALID, null, STATE.toLowerCase()) }
    case apiErrorCodes.ADDRESS_NOT_VALID:
      return {
        message: getUserFriendlyErrorMessage(errorCodes.DOES_NOT_EXIST_NO_REFRESH, null, ADDRESS_TEXT.toLowerCase()),
      }
    case apiErrorCodes.TIME_ZONE_NOT_VALID:
      return { message: getUserFriendlyErrorMessage(errorCodes.ITEM_DOES_NOT_EXIST, null, TIMEZONE) }
    default:
      return {}
  }
}
// @ts-expect-error TS7006 -- MIGRATION: not approved, fix when encountered
const getOtherError = (errorCode, action) => {
  switch (errorCode) {
    case apiErrorCodes.CREDIT_AMOUNT_CHANGED:
      return {
        creditAmountChanged: true,
        message: getUserFriendlyErrorMessage(errorCodes.PATIENT_CREDIT_AMOUNT_CHANGED),
      }
    case apiErrorCodes.VISIT_FORBIDDEN:
      return { message: getUserFriendlyErrorMessage(errorCodes.UNABLE_TO_BOOK_WITH_PROVIDER) }
    case apiErrorCodes.PAYMENT_ERROR:
      return {
        message: getUserFriendlyErrorMessage(
          action.payload.Data.PaymentErrorCode,
          action.payload.Data.PaymentErrorMessage,
        ),
      }
    case apiErrors.CREDIT_CARD_NOT_UNIQUE:
      return { message: getUserFriendlyErrorMessage(errorCodes.DUPLICATE_CREDIT_CARD) }
    case apiErrors.VISIT_RESCHEDULE_NOT_AVAILABLE:
      return { message: getUserFriendlyErrorMessage(errorCodes.CANNOT_CANCEL_VISIT) }
    case apiErrors.TREATMENT_CENTER_NOT_AVAILABLE:
      return { message: getUserFriendlyErrorMessage(errorCodes.TREATMENT_CENTER_NOT_AVAILABLE) }
    case apiErrors.DATE_TIME_NOT_VALID:
      return { message: getUserFriendlyErrorMessage(errorCodes.CAN_NOT_SELECT_VISIT_TIME) }

    default:
      return {}
  }
}
export default (state = initialState, action = {}) => {
  // @ts-expect-error TS2339 -- MIGRATION: not approved, fix when encountered
  switch (action.type) {
    case SEARCH_FOR_PATIENT:
    case GET_PROVIDER_INFO:
    case ADD_CREDIT_CARD:
    case BOOK_VISIT:
    case RESCHEDULE_VISIT:
      return {
        ...state,
        visits: {
          ...state.visits,
          // @ts-expect-error TS2339 -- MIGRATION: not approved, fix when encountered
          [action.extraData.bookingId]: {
            // @ts-expect-error TS7053 -- MIGRATION: not approved, fix when encountered
            ...(state.visits?.[action.extraData.bookingId] || {}),
            loading: true,
            error: '',
          },
        },
      }
    case GET_CREDIT_CARDS:
      return {
        ...state,
        visits: {
          ...state.visits,
          // @ts-expect-error TS2339 -- MIGRATION: not approved, fix when encountered
          [action.extraData.bookingId]: {
            // @ts-expect-error TS7053 -- MIGRATION: not approved, fix when encountered
            ...(state.visits?.[action.extraData.bookingId] || {}),
            loading: true,
            cards: null,
            cardErrorMessage: null,
          },
        },
      }
    case GET_PAYMENT_KEYS_SUCCESS:
      return {
        ...state,
        // @ts-expect-error TS2339 -- MIGRATION: not approved, fix when encountered
        paymentKeys: action.payload,
      }
    case ADD_CREDIT_CARD_SUCCESS:
      return {
        ...state,
        visits: {
          ...state.visits,
          // @ts-expect-error TS2339 -- MIGRATION: not approved, fix when encountered
          [action.extraData.bookingId]: {
            // @ts-expect-error TS7053 -- MIGRATION: not approved, fix when encountered
            ...(state.visits?.[action.extraData.bookingId] || {}),
            loading: false,
          },
        },
      }
    case BOOK_VISIT_SUCCESS:
      return {
        ...state,
        visits: {
          ...state.visits,
          // @ts-expect-error TS2339 -- MIGRATION: not approved, fix when encountered
          [action.extraData.bookingId]: {
            // @ts-expect-error TS7053 -- MIGRATION: not approved, fix when encountered
            ...(state.visits?.[action.extraData.bookingId] || {}),
            loading: false,
          },
        },
      }
    case GET_CREDIT_CARDS_SUCCESS:
      return {
        ...state,
        visits: {
          ...state.visits,
          // @ts-expect-error TS2339 -- MIGRATION: not approved, fix when encountered
          [action.extraData.bookingId]: {
            // @ts-expect-error TS7053 -- MIGRATION: not approved, fix when encountered
            ...(state.visits?.[action.extraData.bookingId] || {}),
            // @ts-expect-error TS2339 -- MIGRATION: not approved, fix when encountered
            cards: action.payload,
            loading: false,
          },
        },
      }
    case RESCHEDULE_VISIT_SUCCESS:
      return {
        ...state,
        isVisitRescheduledSuccess: true,
        visits: {
          ...state.visits,
          // @ts-expect-error TS2339 -- MIGRATION: not approved, fix when encountered
          [action.extraData.bookingId]: {
            // @ts-expect-error TS7053 -- MIGRATION: not approved, fix when encountered
            ...(state.visits?.[action.extraData.bookingId] || {}),
            loading: false,
          },
        },
      }
    case SEARCH_FOR_PATIENT_SUCCESS:
      return {
        ...state,
        visits: {
          ...state.visits,
          // @ts-expect-error TS2339 -- MIGRATION: not approved, fix when encountered
          [action.extraData.bookingId]: {
            // @ts-expect-error TS7053 -- MIGRATION: not approved, fix when encountered
            ...(state.visits?.[action.extraData.bookingId] || {}),
            // @ts-expect-error TS2339 -- MIGRATION: not approved, fix when encountered
            searchResults: { searchId: action.extraData.searchId, results: action.payload },
            loading: false,
          },
        },
      }
    case SET_NEW_VISIT_SELECTED_PATIENT:
      return {
        ...state,
        visits: {
          ...state.visits,
          // @ts-expect-error TS2339 -- MIGRATION: not approved, fix when encountered
          [action.extraData.bookingId]: {
            // @ts-expect-error TS7053 -- MIGRATION: not approved, fix when encountered
            ...(state.visits?.[action.extraData.bookingId] || {}),
            loading: false,
            // @ts-expect-error TS2339 -- MIGRATION: not approved, fix when encountered
            selectedPatient: action.payload,
          },
        },
      }
    case CLEAR_NEW_VISIT_SELECTED_PATIENT:
      return {
        ...state,
        visits: {
          ...state.visits,
          // @ts-expect-error TS2339 -- MIGRATION: not approved, fix when encountered
          [action.extraData.bookingId]: {
            // @ts-expect-error TS7053 -- MIGRATION: not approved, fix when encountered
            ...(state.visits?.[action.extraData.bookingId] || {}),
            selectedPatient: null,
          },
        },
      }
    case GET_PROVIDER_INFO_SUCCESS:
      return {
        ...state,
        visits: {
          ...state.visits,
          // @ts-expect-error TS2339 -- MIGRATION: not approved, fix when encountered
          [action.extraData.bookingId]: {
            // @ts-expect-error TS7053 -- MIGRATION: not approved, fix when encountered
            ...(state.visits?.[action.extraData.bookingId] || {}),
            // @ts-expect-error TS2339 -- MIGRATION: not approved, fix when encountered
            provider: action.payload,
            loading: false,
          },
        },
      }

    case SEARCH_FOR_PROVIDER_AVAILABILITY:
      return {
        ...state,
        visits: {
          ...state.visits,
          // @ts-expect-error TS2339 -- MIGRATION: not approved, fix when encountered
          [action.extraData.bookingId]: {
            // @ts-expect-error TS7053 -- MIGRATION: not approved, fix when encountered
            ...(state.visits?.[action.extraData.bookingId] || {}),
            loading: true,
            error: '',
          },
        },
      }
    case SEARCH_FOR_PROVIDER_AVAILABILITY_SUCCESS:
      return {
        ...state,
        visits: {
          ...state.visits,
          // @ts-expect-error TS2339 -- MIGRATION: not approved, fix when encountered
          [action.extraData.bookingId]: {
            // @ts-expect-error TS7053 -- MIGRATION: not approved, fix when encountered
            ...(state.visits?.[action.extraData.bookingId] || {}),
            searchResultsProviders: {
              // @ts-expect-error TS2339 -- MIGRATION: not approved, fix when encountered
              searchId: action.extraData.searchId,
              // @ts-expect-error TS2339 -- MIGRATION: not approved, fix when encountered
              results: action.payload,
            },
            loading: false,
          },
        },
      }
    case SEARCH_FOR_PROVIDER_AVAILABILITY_ERROR:
      return {
        ...state,
        visits: {
          ...state.visits,
          // @ts-expect-error TS2339 -- MIGRATION: not approved, fix when encountered
          [action.extraData.bookingId]: {
            // @ts-expect-error TS7053 -- MIGRATION: not approved, fix when encountered
            ...(state.visits?.[action.extraData.bookingId] || {}),
            loading: false,
            // @ts-expect-error TS2339 -- MIGRATION: not approved, fix when encountered
            error: getUserFriendlyErrorMessage(action.payload.UserFriendlyErrorCode || action.payload.ErrorCode),
          },
        },
      }

    case SEARCH_FOR_PATIENT_ERROR:
    case GET_PROVIDER_INFO_ERROR:
    case GET_CREDIT_CARDS_ERROR:
    case ADD_CREDIT_CARD_ERROR:
    case BOOK_VISIT_ERROR:
    case RESCHEDULE_VISIT_ERROR:
      // @ts-expect-error TS2339 -- MIGRATION: not approved, fix when encountered
      if (action.extraData.adyen) {
        // @ts-expect-error TS2339 -- MIGRATION: not approved, fix when encountered
        action.extraData.adyen.unlockFormFieldsOnPaymentError()
      }
      let errorObject = {}
      // @ts-expect-error TS2339 -- MIGRATION: not approved, fix when encountered
      if (action.payload.UserFriendlyErrorCode || action.payload.UserFriendlyErrorCode === '') {
        errorObject = {
          message:
            // @ts-expect-error TS2339 -- MIGRATION: not approved, fix when encountered
            action.payload.UserFriendlyErrorCode ?
              // @ts-expect-error TS2339 -- MIGRATION: not approved, fix when encountered
              getUserFriendlyErrorMessage(action.payload.UserFriendlyErrorCode, null, action.payload.extra)
            : '',
          // @ts-expect-error TS2339 -- MIGRATION: not approved, fix when encountered
          defaultServerErrorMessage: action.payload.UserFriendlyErrorCode ? action.payload : '',
        }
      } else {
        errorObject = {
          // @ts-expect-error TS2339 -- MIGRATION: not approved, fix when encountered
          ...getNotFoundError(action.payload.ErrorCode),
          // @ts-expect-error TS2554 -- MIGRATION: not approved, fix when encountered
          ...getValidationError(action.payload.ErrorCode),
          // @ts-expect-error TS2339 -- MIGRATION: not approved, fix when encountered
          ...getOtherError(action.payload.ErrorCode, action),
        }
        // @ts-expect-error TS2339 -- MIGRATION: not approved, fix when encountered
        if (!errorObject.message) {
          errorObject = {
            message: getUserFriendlyErrorMessage(errorCodes.DEFAULT),
            // @ts-expect-error TS2339 -- MIGRATION: not approved, fix when encountered
            defaultServerErrorMessage: action.payload,
          }
        }
      }
      // @ts-expect-error TS2339 -- MIGRATION: not approved, fix when encountered
      if (action.type === GET_CREDIT_CARDS_ERROR) {
        return {
          ...state,
          visits: {
            ...state.visits,
            // @ts-expect-error TS2339 -- MIGRATION: not approved, fix when encountered
            [action.extraData.bookingId]: {
              // @ts-expect-error TS7053 -- MIGRATION: not approved, fix when encountered
              ...state.visits[action.extraData.bookingId],
              loading: false,
              // @ts-expect-error TS2339 -- MIGRATION: not approved, fix when encountered
              error: errorObject.message,
              // @ts-expect-error TS2339 -- MIGRATION: not approved, fix when encountered
              cardErrorMessage: errorObject.message,
            },
          },
          // @ts-expect-error TS2339 -- MIGRATION: not approved, fix when encountered
          defaultServerErrorMessage: errorObject.defaultServerErrorMessage,
        }
      }
      // @ts-expect-error TS2339 -- MIGRATION: not approved, fix when encountered
      if (errorObject.creditAmountChanged) {
        return {
          ...state,
          visits: {
            ...state.visits,
            // @ts-expect-error TS2339 -- MIGRATION: not approved, fix when encountered
            [action.extraData.bookingId]: {
              // @ts-expect-error TS7053 -- MIGRATION: not approved, fix when encountered
              ...state.visits[action.extraData.bookingId],
              loading: false,
              // @ts-expect-error TS2339 -- MIGRATION: not approved, fix when encountered
              error: errorObject.message,
            },
          },
          // @ts-expect-error TS2339 -- MIGRATION: not approved, fix when encountered
          defaultServerErrorMessage: errorObject.defaultServerErrorMessage,
        }
      }
      return {
        ...state,
        visits: {
          ...state.visits,
          // @ts-expect-error TS2339 -- MIGRATION: not approved, fix when encountered
          [action.extraData.bookingId]: {
            // @ts-expect-error TS7053 -- MIGRATION: not approved, fix when encountered
            ...state.visits[action.extraData.bookingId],
            loading: false,
            // @ts-expect-error TS2339 -- MIGRATION: not approved, fix when encountered
            error: errorObject.message,
          },
        },
        // @ts-expect-error TS2339 -- MIGRATION: not approved, fix when encountered
        defaultServerErrorMessage: errorObject.defaultServerErrorMessage,
      }
    case DELETE_BOOKING:
      const cloneVisits = JSON.parse(JSON.stringify(state.visits))
      // @ts-expect-error TS18046 -- MIGRATION: not approved, fix when encountered
      delete cloneVisits[action.payload.bookingId]
      return {
        ...state,
        // @ts-expect-error TS2698 -- MIGRATION: not approved, fix when encountered
        visits: { ...cloneVisits },
      }
    case SET_IS_VISIT_RESCHEDULED_SUCCESS:
      return {
        ...state,
        // @ts-expect-error TS2339 -- MIGRATION: not approved, fix when encountered
        isVisitRescheduledSuccess: action.payload,
      }
    case SET_ERROR:
      return {
        ...state,
        visits: {
          ...state.visits,
          // @ts-expect-error TS2339 -- MIGRATION: not approved, fix when encountered
          [action.payload.bookingId]: {
            // @ts-expect-error TS7053 -- MIGRATION: not approved, fix when encountered
            ...state.visits[action.payload.bookingId],
            // @ts-expect-error TS2339 -- MIGRATION: not approved, fix when encountered
            error: action.payload.error,
          },
        },
      }
    case LOGOUT:
      return { ...initialState }
    case GET_PAYMENT_KEYS:
    case GET_PAYMENT_KEYS_ERROR:
      return {
        ...state,
      }
    default:
      return state
  }
}
